import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

// Bernie
import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, ContextStore, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';
import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';

// Stores
import { SearchResultsStore } from 'stores/search-results-store';

// UITK
import { UitkIcon } from 'uitk-react-icons';
import { UitkButton } from 'uitk-react-button';
import { UitkLinkAlign } from 'uitk-react-link';

// Components
import { SearchForm } from 'components/common/search-form/index';

const LinkTracked = ClickTracker(Link);
const ButtonTracked = ClickTracker(UitkButton);

export interface PlaybackProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  routeStore?: RouteStore;
  searchResults?: SearchResultsStore;
  context?: ContextStore;
  bookingNextProperty?: boolean;
  shouldShowMultiItemWizardFields?: boolean;
}

export interface PlaybackState {
  jsEnabled: boolean;
  showDatepicker: boolean;
  searchFormExpanded: boolean;
}

export enum DisplayState {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN'
}
@withLocalization
@inject('analytics', 'context', 'routeStore', 'searchResults')
@observer
export class Playback extends React.Component<PlaybackProps, PlaybackState> {
  constructor(props) {
    super(props);

    this.afterSubmit = this.afterSubmit.bind(this);
    this.handleExpand = this.handleExpand.bind(this);
    this.onUpdateResults = this.onUpdateResults.bind(this);

    const { searchResults } = props;

    const searchFormDisplayOptions =
      searchResults && searchResults.searchAndFilterDisplayOptions && searchResults.searchAndFilterDisplayOptions.searchFormDisplayOptions;

    // Set on server.
    const searchFormExpanded =
      searchFormDisplayOptions &&
      searchFormDisplayOptions.searchForm &&
      searchFormDisplayOptions.searchForm.displayState === DisplayState.OPEN;

    // Set on client.
    const expandForm = searchResults && searchResults.expandForm;

    const showDatepicker =
      searchFormDisplayOptions &&
      searchFormDisplayOptions.datePicker &&
      searchFormDisplayOptions.datePicker.displayState === DisplayState.OPEN;

    this.state = {
      jsEnabled: false,
      showDatepicker,
      searchFormExpanded: searchFormExpanded || expandForm
    };
  }

  public componentDidMount() {
    this.setState({ jsEnabled: true });
  }

  public componentWillUnmount() {
    const { searchResults } = this.props;

    if (searchResults) {
      searchResults.expandForm = false;
    }
  }

  public componentDidUpdate() {
    const { routeStore } = this.props;
    const updateSearch = routeStore && routeStore.updateSearch && typeof routeStore.updateSearch === 'function';
    const startDate = routeStore && routeStore.queryParams && routeStore.queryParams.startDate;

    if (this.state.showDatepicker && updateSearch && startDate) {
      this.setState({ showDatepicker: false });
      routeStore.updateSearch({ pwaDialog: 'datepicker-start' });
    }
  }

  get searchDateRange() {
    const { l10n, searchResults } = this.props;
    const { searchCriteria } = searchResults;

    if (!searchCriteria || !searchCriteria.startDate || !searchCriteria.endDate) {
      return '';
    }

    const startDate = l10n.formatDateString(searchCriteria.startDate, { skeleton: 'MMMd' });
    const endDate = l10n.formatDateString(searchCriteria.endDate, { skeleton: 'MMMd' });

    return l10n.formatText('hotels.searchResults.playback.dateRange', startDate, endDate);
  }

  private afterSubmit() {
    this.setState({ searchFormExpanded: true });
  }

  private onUpdateResults() {
    this.props.searchResults.clearDefaultRequestFields();
    this.props.searchResults.shouldFetch = true;
  }

  private handleExpand() {
    this.setState(
      {
        searchFormExpanded: !this.state.searchFormExpanded
      },
      () => {
        this.sendTracking(this.state.searchFormExpanded ? 'ChangeSearch' : 'CloseForm');
      }
    );
  }

  private sendTracking(event) {
    const { analytics } = this.props;
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(`:${event}`, 'Edit search form', analytics.omnitureData, null);
    }
  }

  public render() {
    const { jsEnabled } = this.state;
    const { searchResults, l10n, bookingNextProperty, shouldShowMultiItemWizardFields } = this.props;
    const { searchCriteria } = searchResults;
    const destination = searchCriteria && searchCriteria.destination;
    const isLoading = searchResults && searchResults.isLoading;
    const searchedDestination = searchResults && searchResults.searchedDestination;
    const destinationMessage = isLoading
      ? destination
      : destination || searchedDestination || <LocalizedText message="hotels.searchResults.playback.destinationMissing.text" />;
    const guestCount = searchCriteria && searchCriteria.guestCount;

    const playbackSubHeading = (
      <div className="playback__subheading">
        <div className="is-visually-hidden">{this.searchDateRange}</div>
        <span className="playback__date-range" aria-hidden="true">
          {this.searchDateRange}
        </span>
        {guestCount > 0 && (
          <span className="playback__guest-count">
            <LocalizedText
              message={guestCount === 1 ? 'hotels.searchResults.singleGuest' : 'hotels.searchResults.numberOfGuests'}
              data={[guestCount]}
            />
          </span>
        )}
      </div>
    );

    const iconAndSubHeading = (
      <React.Fragment>
        <UitkIcon
          className="playback__icon"
          description={l10n.formatText('hotels.searchResults.backToSearchResults.a11yLabel')}
          id="playback-icon"
          name="search"
          title={l10n.formatText('hotels.searchResults.title')}
        />
        <div className="playback__title">
          <h1 className="playback__heading truncate" data-stid="content-location-title">
            {destinationMessage}
          </h1>
          {playbackSubHeading}
        </div>
      </React.Fragment>
    );

    const playBackLink = (
      <LinkTracked
        className="playback__section playback__action all-l-margin-three"
        linkName="Edit search form"
        rfrr=":ChangeSearch"
        to="/Hotels"
      >
        {iconAndSubHeading}
      </LinkTracked>
    );

    const playbackExpander = (
      <div onClick={this.handleExpand} className="playback__section playback__action form_expander all-l-margin-three">
        {iconAndSubHeading}
      </div>
    );

    const closeForm = (
      <div className="uitk-grid all-cell-1-6 all-grid-align-center">
        <div className="uitk-cell all-b-padding-two all-t-padding-two">
          <ButtonTracked
            linkName="Close search form"
            onClick={this.handleExpand}
            align={UitkLinkAlign.CENTER}
            className="uitk-button-tertiary"
            data-stid="close-search-form"
          >
            <LocalizedText message="hotels.searchResults.close.text" />
          </ButtonTracked>
        </div>
      </div>
    );

    const searchForm = (
      <ChangeSearchForm
        onUpdateResults={this.onUpdateResults}
        afterSubmit={this.afterSubmit}
        {...{ shouldShowMultiItemWizardFields, searchResults, bookingNextProperty }}
      />
    );

    return (
      <Viewport>
        <ViewSmall>
          <div className="playback" data-stid="section-hotel-search">
            {jsEnabled ? (this.state.searchFormExpanded ? searchForm : playbackExpander) : playBackLink}
            {this.state.searchFormExpanded && closeForm}
          </div>
        </ViewSmall>
        <ViewMedium>{searchForm}</ViewMedium>
      </Viewport>
    );
  }
}

export const ChangeSearchForm = ({
  searchResults,
  onUpdateResults,
  afterSubmit,
  bookingNextProperty = false,
  shouldShowMultiItemWizardFields
}) => {
  const sortAndFilter = searchResults && searchResults.sortAndFilter;
  const displayFields = ['destination', 'dateRange', 'rooms', 'multiItemCarPickUpTime', 'multiItemCarDropOffTime'];
  const ignoreFields = ['startDate', 'endDate'];
  const wrapperClasses = Object.keys(displayFields).map(key => ({
    [key]: 'uitk-cell all-y-gutter-two m-cell-1-1 l-cell-1-3 xl-cell-1-3'
  }));
  const searchFormProps = {
    className: 'uitk-grid l-x-gutter-three all-x-gutter-two all-y-gutter-two',
    model: searchResults && searchResults.searchCriteria,
    sortAndFilterOptions: sortAndFilter && sortAndFilter.options,
    sortAndFilterDefaults: sortAndFilter && sortAndFilter.defaults,
    provideDefaultSubmitButton: true,
    autoSubmit: true,
    ...{ shouldShowMultiItemWizardFields, afterSubmit, ignoreFields, displayFields, onUpdateResults, wrapperClasses }
  };

  return (
    <div className="all-r-padding-two all-l-padding-two all-b-padding-two all-t-padding-two" data-stid="section-hotel-search">
      {bookingNextProperty && (
        <p className="uitk-type-heading-400 all-b-padding-two pwa-theme--grey-700">
          <LocalizedText message="hotels.searchResults.multiProperty.searchCriteria.header" />
        </p>
      )}
      <SearchForm {...searchFormProps} />
    </div>
  );
};
